import { registerSW } from "virtual:pwa-register";
import "./style.css";
// After the screens: the frame is what wins where the two meet.
import "./shell.css";
import { openStore, pageStores, type Store } from "./data/store.js";
import { eraseSignedInShares } from "./data/mirror.js";
import {
  currentUser,
  pendingSignIn,
  pendingLogout,
  PENDING_LOGOUT,
  settlePendingLogout,
  forgetUser,
  rememberedUser,
  rememberUser,
  type User,
} from "./lib/auth.js";
import { boot } from "./lib/boot.js";
import { signOutAndWipe } from "./lib/wipe.js";
import { DELETION_KEY, pendingDeletion, saveDeletion, type AccountDeletion } from "./lib/accountDeletion.js";
import { accountDeletionScreen } from "./views/accountDeletion.js";
import { heldOutsideFrame, updateCallbacks } from "./lib/update.js";
import { el } from "./lib/dom.js";
import { environmentChip, titled } from "./lib/environment.js";
import { addedPath, currentRoute, onNavigationCommit, screenKey, startRouting, type Navigation, type Route, type NavigationGuard } from "./lib/router.js";
import { confirmScreen, signInScreen, unreachableScreen, type UnreachableScreen } from "./views/auth.js";
import { addedListScreen, noteAddedNow } from "./views/addedList.js";
import { agendaScreen } from "./views/agenda.js";
import { listScreen } from "./views/list.js";
import { listsScreen } from "./views/lists.js";
import { noteViewerScreen } from "./views/noteViewer.js";
import { activityScreen } from "./views/activity.js";
import { settingsScreen } from "./views/settings.js";
import { createChrome, createStandaloneChrome, type Chrome } from "./views/shell.js";
import { notFound, type Screen, type ScreenHost, type Shell } from "./views/ui.js";
import { dropAddForSignIn, shareGrantId, shareTokenFromHash, type ShareCredential } from "./lib/sharing.js";
import { startSharedVisit, type SharedVisit, type VisitState } from "./lib/sharedVisit.js";
import { sharedListScreen, sharedStateScreen, sharedStatus } from "./views/sharedPage.js";

/**
 * Work out who the caller is, open their local mirror, then hand each route its
 * screen — inside the chrome, once there is somebody to draw it for.
 *
 * Screens read from the store and write to it, and never touch the network —
 * the sync engine behind the store is the only thing that does. That is what
 * makes every screen work in a lift, and it is why the store is opened here,
 * once, rather than per screen: it holds the queue, and a second copy of the
 * queue is a second copy of the truth.
 */

const root = document.querySelector<HTMLElement>("#app");

let showing: Screen | undefined;
/** Which screen is up, ignoring what is selected on it. See `screenKey`. */
let showingKey: string | undefined;
/** The navigation being rendered, read once by `render`. */
let arriving: Navigation | undefined;
onNavigationCommit((navigation) => { arriving = navigation; });
/**
 * The item whose note page the person is coming back from (g77), by its
 * way back or by Back, and not by going somewhere else from it. The new
 * screen puts the focus on that item's row.
 */
let returning: string | undefined;

function release(): void {
  // Whatever was on screen is letting go of the store now. A watcher that
  // outlives its DOM is a leak on every navigation and, worse, a second screen
  // quietly patching nodes nobody can see.
  showing?.dispose();
  showing = undefined;
  showingKey = undefined;
}

// index.html is the same document in every environment and its title says
// "Trayflux". On test the brand is "Trayflux Test", from the first script on.
document.title = titled();

/**
 * The screens reachable without a session: no rail, no navigation, nothing to
 * navigate to. They keep the pre-shell behaviour of being the whole page.
 */
function mountBare(screen: Screen | Node): void {
  release();
  showing = screen instanceof Node ? undefined : screen;

  const node = screen instanceof Node ? screen : screen.node;
  // The screen says how it wants to sit: one short prompt is centred in the
  // viewport, a list starts at the top and grows down.
  const centered = node instanceof Element && node.classList.contains("centered");
  document.body.classList.toggle("centered", centered);
  document.body.classList.remove("shell");
  // Every screen outside the shell comes through here, so this is where they
  // all get the environment chip (g93): sign-in, the page a share link opens,
  // and every dead end. Inside `main`, so it is within a landmark.
  root?.replaceChildren(...environmentChip("page"), node);
}

function mountInChrome(chrome: Chrome, screen: Screen, route: Route): void {
  release();
  showing = screen;
  showingKey = screenKey(route);

  // The chrome is built once and outlives every screen mounted into it — but a
  // reauthentication puts the sign-in screen over the whole page, so it can be
  // off the document and need putting back.
  if (!chrome.node.isConnected) {
    document.body.classList.remove("centered");
    document.body.classList.add("shell");
    root?.replaceChildren(chrome.node);
  }
  chrome.mount(screen, route);
  const from = returning;
  returning = undefined;
  // A deep link straight to an item is a fresh mount carrying a selection, and
  // the screen has to be told about it by the same path a later navigation
  // uses — otherwise the panel only ever opens for people who got there by
  // tapping.
  screen.update?.(route);
  if (from !== undefined) screen.returnTo?.(from);
}

let shell: Shell | undefined;
let chrome: Chrome | undefined;
let deleting = false;
let deletingUserId: string | undefined;
let openingUserId: string | undefined;

function showDeletion(state: AccountDeletion, sendNow = false): void {
  deleting = true;
  deletingUserId = state.user.id;
  const previous = shell;
  shell = undefined;
  release();
  chrome?.dispose();
  chrome = undefined;
  const stopped = previous?.store.close() ?? Promise.resolve();
  mountBare(accountDeletionScreen(state, () => stopped, sendNow));
}

window.addEventListener("storage", event => {
  if (event.key !== DELETION_KEY || !event.newValue) return;
  try {
    const state = pendingDeletion();
    const activeUserId = openingUserId ?? shell?.user.id ?? deletingUserId;
    if (state && activeUserId === state.user.id) showDeletion(state);
  } catch { invalidDeletion(); }
});

/**
 * Another tab signed out (g65). This one is drawing lists the device no longer
 * holds, over a cookie that may still open them, so it lets go the way a
 * deletion does and starts again: the start sends the pending logout itself,
 * or shows sign-in when it cannot. A store still opening is told by `session`,
 * because by the time it opens the note may already be settled and gone.
 */
let session = 0;

window.addEventListener("storage", event => {
  if (event.key !== PENDING_LOGOUT || !event.newValue) return;
  if (!shell && !openingUserId) return;
  session += 1;
  const previous = shell;
  shell = undefined;
  release(); chrome?.dispose(); chrome = undefined;
  void previous?.store.close();
  // The other tab forgot the user, but this one may have written it back
  // while opening, and a fast logout can clear the note before this event
  // arrives -- which would leave the start nothing to stop it reopening.
  forgetUser();
  void start();
});

function invalidDeletion(): void {
  deleting = true;
  deletingUserId = undefined;
  const previous = shell;
  shell = undefined;
  release(); chrome?.dispose(); chrome = undefined;
  void previous?.store.close();
  mountBare(el("section", { class: "screen centered", children: [
    el("h1", { text: "Browser cleanup needs attention" }),
    el("p", { text: "Trayflux could not read its saved account cleanup information. Clear this site's data in your browser settings, then reload. Your server account deletion has not been confirmed." }),
  ] }));
}

function openSession(store: Store, user: User): { shell: Shell; chrome: Chrome } {
  const frame = createChrome({
    store,
    user,
    reauth,
    deleteAccount: async () => {
      if (showing?.dirty?.() || showing?.busy?.()) {
        throw new Error("Finish the open form before deleting your account.");
      }
      const state: AccountDeletion = { user: { ...user }, phase: "pending" };
      saveDeletion(state);
      showDeletion(state, true);
    },
  });
  return {
    chrome: frame,
    shell: {
      store,
      user,
      signOut: () => void signOutAndWipe({
        // The account's shared-list copies go with it (g71); a guest's stay.
        destroy: async () => { await store.destroy(); await eraseSignedInShares(); },
        leave: () => { window.location.href = "/"; },
      }),
      reauth,
      updateUser: frame.updateUser,
      lend: frame.lend,
      detail: frame.detail,
      announce: frame.announce,
      openPopup: frame.openPopup,
      confirm: frame.confirm,
      refreshAction: frame.refreshAction,
      narrow: frame.narrow,
      onNarrow: frame.onNarrow,
    },
  };

  function reauth(): void {
    // One more attempt first: the session may have been renewed in another
    // tab, in which case the queue drains and there is nothing to sign into.
    store.resume();
    mountSignIn();
  }
}

function render(route: Route): void {
  const came = arriving;
  arriving = undefined;
  const active = shell;
  const frame = chrome;
  if (!active || !frame) return;
  returning = came?.from.name === "note" && (came.traversal || came.own) &&
    screenKey(came.from) !== screenKey(route) ? came.from.item : undefined;

  // Same screen, different selection: the list stays exactly where it is and
  // is told about the route instead of being built again underneath the panel
  // that route just opened.
  if (showing?.update && showingKey === screenKey(route)) {
    frame.setRoute(route);
    showing.update(route);
    return;
  }

  switch (route.name) {
    case "lists":
      mountInChrome(frame, listsScreen(active), route);
      return;
    case "list":
      mountInChrome(frame, listScreen(active, route.id), route);
      return;
    case "today":
    case "upcoming":
      mountInChrome(frame, agendaScreen(active, route.name), route);
      return;
    case "settings":
      mountInChrome(frame, settingsScreen(active, frame.account), route);
      return;
    case "note":
      mountInChrome(frame, noteViewerScreen(active, route), route);
      return;
    case "activity":
      mountInChrome(frame, activityScreen(active, route), route);
      return;
    case "shared":
      // A list in the account's Lists (g137); a link's own page never reaches here.
      if (route.grant !== undefined) {
        mountInChrome(frame, addedListScreen(active, route.grant), route);
        return;
      }
      mountInChrome(frame, { node: el("section", { class: "screen", children: notFound() }), dispose() {} }, route);
      return;
    case "confirm":
    case "unknown":
      mountInChrome(frame, { node: el("section", { class: "screen", children: notFound() }), dispose() {} }, route);
      return;
  }
}

/** Asked before history or the mounted screen commits a departure. */
const guardDeparture: NavigationGuard = navigation => {
  if (!chrome?.node.isConnected || !showing || showingKey === screenKey(navigation.to)) return;
  if (showing.busy?.()) return (_proceed, cancel) => { cancel(); };
  if (chrome.navigationBlocked()) return (_proceed, cancel) => {
    if (navigation.traversal) chrome?.cancelQuestion();
    cancel();
  };
  if (!chrome.navigationDirty() && !showing.dirty?.()) return;
  return (proceed, cancel) => chrome!.askDeparture(proceed, cancel);
};

async function begin(user: User): Promise<void> {
  try {
    const deletion = pendingDeletion();
    if (deletion?.user.id === user.id) { showDeletion(deletion); return; }
  } catch { invalidDeletion(); return; }
  openingUserId = user.id;
  rememberUser(user);

  // One store per document, and the router already expects to be started twice
  // — an existing session and a just-redeemed magic link are two paths here.
  // A second store over the same database would drain one queue from two
  // engines; a second store for a *different* account would leave the previous
  // one's engine running under the new session, pushing their queued mutations
  // as this user. The previous mirror is closed, never deleted: they did not
  // sign out, and whatever they had queued is still theirs to send.
  const opening = session;
  const previous = shell;
  if (previous) {
    if (previous.user.id === user.id) {
      chrome?.updateUser(user);
      startRouting(render, guardDeparture);
      openingUserId = undefined;
      return;
    }
    shell = undefined;
    release();
    chrome?.dispose();
    chrome = undefined;
    // The history it kept goes, though its queue stays (g110): history is
    // read again whenever it is shown, and a page another account's session
    // left on this device is not one to keep.
    await previous.store.forgetActivity().catch(() => undefined);
    await previous.store.close().catch(() => undefined);
  }

  try {
    const store = await openStore(user.id);
    if (opening !== session) {
      await store.close();
      return;
    }
    // A storage event can stop this account while IndexedDB is still opening.
    // Do not let that late startup recreate its mirror or put its shell back.
    if (deleting) {
      // The deletion screen owns the outcome, including a blocked wipe, so a
      // rejection here must not fall through to the storage dead end below.
      if (deletingUserId === user.id) await store.destroy().catch(() => undefined);
      else await store.close();
      return;
    }
    const opened = openSession(store, user);
    shell = opened.shell;
    chrome = opened.chrome;
  } catch {
    // No IndexedDB means no mirror, and every screen reads from the mirror.
    // Safari in private browsing and a browser with site data blocked both land
    // here, and both are a dead end worth naming rather than a blank page.
    mountBare(
      el("section", {
        class: "screen centered",
        children: [
          el("h1", { text: "Trayflux" }),
          el("p", {
            class: "muted",
            text:
              "This browser will not let Trayflux store anything on this device, " +
              "and the app keeps your lists here. Allow site data, or try another browser.",
          }),
        ],
      }),
    );
    return;
  } finally { if (openingUserId === user.id) openingUserId = undefined; }

  startRouting(render, guardDeparture);
}

function start(retrying?: UnreachableScreen): Promise<void> {
  try {
    const deletion = pendingDeletion();
    const saved = rememberedUser();
    if (deletion && (!saved || saved.id === deletion.user.id)) { showDeletion(deletion); return Promise.resolve(); }
  } catch { invalidDeletion(); return Promise.resolve(); }
  // A retry belongs to the screen that requested it. Once another screen
  // replaces it, its outstanding server response must not change the page.
  const active = () => !retrying || showing === retrying;
  return boot({
    route: currentRoute(),
    remembered: rememberedUser,
    pendingSignIn,
    pendingLogout: () => pendingLogout() !== undefined,
    settleLogout: settlePendingLogout,
    forget: forgetUser,
    currentUser,
    confirm: (token) =>
      mountBare(
        confirmScreen(token, (signedIn) => {
          // Drop the token out of the URL before the app renders over it. It
          // is spent, but a spent token in the history is still a token in the
          // history -- and in the back button.
          window.history.replaceState({}, "", "/");
          completeSignIn(signedIn);
        }),
      ),
    begin: async (user) => { if (active()) await begin(user); },
    shared: () => { if (active()) mountShared(); },
    linked: () => shareTokenFromHash(window.location.hash) !== undefined,
    signIn: () => { if (active()) mountSignIn(); },
    unreachable: (error) => {
      if (!active()) return;
      // boot reports a failed request through this callback, then resolves.
      // Reuse the mounted screen so both focus and its live region survive.
      if (retrying) {
        retrying.retryFailed(error);
      } else {
        const screen = unreachableScreen(error, () => start(screen));
        mountBare(screen);
      }
    },
  });
}

// ---- a shared list's page (g72) --------------------------------------------

let visit: SharedVisit | undefined;

/**
 * A shared list's page is someone's list: no search engine indexes it, and
 * it sends no Referer. Caddy says the same in headers; these say it wherever
 * the page is served without them, a preview included.
 */
function keepSharedPagePrivate(): void {
  for (const [name, content] of [["robots", "noindex, nofollow"], ["referrer", "no-referrer"]] as const) {
    if (document.head.querySelector(`meta[name="${name}"]`)) continue;
    document.head.append(el("meta", { attrs: { name, content } }));
  }
}

/**
 * Opens the list the page's link names, with no account (g72). Reading
 * asks nobody for a name; the first change does. The frame is the account's
 * less its navigation, and it is built once the list's copy is open --
 * before that, the page is one of the states a link can be in.
 */
function mountShared(): void {
  keepSharedPagePrivate();
  const status = sharedStatus();
  document.body.append(status.node);
  const showState = (kind: Exclude<VisitState["kind"], "open">, retry: () => void): void => {
    mountBare(sharedStateScreen(kind, retry));
    status.say(kind);
  };
  const token = shareTokenFromHash(window.location.hash);
  // A link's path names its grant (g131). One that names another grant has
  // been altered, so it opens nothing, and asks the server nothing either.
  const route = currentRoute();
  const pathGrant = route.name === "shared" ? route.grant : undefined;
  // With no link at all, the path's grant is a list in the visitor's Lists
  // (g132): opened by the account's session, or refused by the server.
  const credential: ShareCredential | undefined = token
    ? (pathGrant === undefined || pathGrant === shareGrantId(token) ? { kind: "link", token } : undefined)
    : pathGrant !== undefined ? { kind: "added", grantId: pathGrant } : undefined;
  if (!credential) {
    showState("unavailable", () => undefined);
    return;
  }
  const current = startSharedVisit(credential);
  visit = current;
  const show = (state: VisitState): void => {
    if (visit !== current) return;
    // An open page never drops back to a state screen: its copy is what it
    // shows, offline and after its link ends alike.
    if (chrome) return;
    if (state.kind !== "open") {
      showState(state.kind, state.kind === "signin" ? signInOverShared : () => current.retry());
      return;
    }
    status.dispose();
    const { store } = state;
    const frame = createStandaloneChrome({ store, reauth: signInOverShared });
    chrome = frame;
    const host: ScreenHost = {
      store,
      detail: frame.detail,
      announce: frame.announce,
      openPopup: frame.openPopup,
      confirm: frame.confirm,
      refreshAction: frame.refreshAction,
      narrow: frame.narrow,
      onNarrow: frame.onNarrow,
    };
    mountInChrome(frame, sharedListScreen(host, current, store, {
      signIn: signInOverShared,
      signInToAdd: signInToAddShared,
      ...(credential.kind === "link" ? { listed: (justAdded: boolean) => handOver(shareGrantId(credential.token), store, justAdded) } : {}),
      gone: () => {
        release();
        chrome?.dispose();
        chrome = undefined;
        visit = undefined;
        void current.close();
        document.body.append(status.node);
        showState("unavailable", () => undefined);
      },
    }), currentRoute());
    startRouting(renderShared, guardDeparture);
  };
  current.onState(show);
  show(current.state());
}

/**
 * A link whose list is in the signed-in visitor's Lists hands over to their
 * app (g137), which opens the same copy and queue as one of their lists, with
 * the link out of the address and an item it named kept. Not over a draft:
 * it waits until nothing typed would be lost and what the page wrote is
 * stored, both checked in the task that replaces the page. A loop of its
 * own rather than `applyWhenSettled`, which holds one action for the page
 * and would let an update waiting there swallow this one.
 */
const HAND_OVER_RECHECK_MS = 500;
let handingOver = false;
function handOver(grantId: string, store: Store, justAdded: boolean): void {
  if (handingOver) return;
  handingOver = true;
  if (justAdded) noteAddedNow(grantId);
  const go = (): void => {
    if (!clean() || (showing?.busy?.() ?? false)) { setTimeout(go, HAND_OVER_RECHECK_MS); return; }
    if (!store.stored()) { void store.whenStored().then(go, go); return; }
    const route = currentRoute();
    const item = route.name === "shared" && route.itemId !== undefined ? `/i/${encodeURIComponent(route.itemId)}` : "";
    window.location.replace(`${addedPath(grantId)}${item}`);
  };
  go();
}

/**
 * Inside the page, a selection; anywhere else, the app from the start. The
 * page holds no account, so another route is a fresh load of whatever the
 * address now says.
 */
function renderShared(route: Route): void {
  if (route.name !== "shared") {
    window.location.reload();
    return;
  }
  chrome?.setRoute(route);
  showing?.update?.(route);
}

/**
 * A visitor whose sign-in expired asked to sign in again. Afterwards the
 * page starts over with the same link, which is still in the address, and
 * the server says who is looking now.
 */
function signInOverShared(): void {
  mountBare(signInScreen(() => window.location.reload(), () => window.location.reload(), "Back to the shared list"));
}

/**
 * A guest chose Add to my Lists (g132), and the server holds that for the
 * sign-in that finishes next. A sign-in that added it sends the app to the
 * list in their Lists; one that did not starts the page over with the link,
 * which says what is true now. Going back drops the add, so a later sign-in
 * in this browser adds nothing.
 */
function signInToAddShared(): void {
  mountBare(signInScreen(() => window.location.reload(), () => {
    void dropAddForSignIn().catch(() => undefined).finally(() => window.location.reload());
  }, "Back to the shared list", true));
}

function completeSignIn(user: User): void {
  if (shell?.user.id === user.id) shell.store.resume();
  void begin(user);
}

function mountSignIn(): void {
  const saved = rememberedUser();
  mountBare(signInScreen(completeSignIn, saved ? () => {
    void begin(saved);
  } : undefined));
}

void start();

/**
 * Whether a reload right now would lose something typed. Inside the frame:
 * a draft in a row or the list's name (the screen's), Details, the popup, or
 * a discard question up over either (the frame's). Outside it -- the sign-in
 * form before there is a frame, or over the frame after a session expires --
 * any text field holding a value, because the bare screens have no draft
 * reading of their own.
 */
function clean(): boolean {
  if (deleting) return false;
  const framed = chrome?.node.isConnected ?? false;
  if (!framed) return !heldOutsideFrame();
  return !chrome!.dirty() && !(showing?.dirty?.() ?? false);
}

// registerType: 'prompt' — the new worker installs and then waits. It is let
// through the moment nothing on the page would be lost by the reload that
// follows, and not before (Q04). `updateSW` is bound before `onNeedRefresh`
// can fire: registration is asynchronous, and the callback closes over it.
//
// Both wait for the stores' writes as well as for drafts (g67): Enter or Save
// ends the draft a few milliseconds before IndexedDB has the edit.
//
// The reload is gated the same way, and separately: Chromium can defer the
// activation it was told to skip to -- measured, when the old worker was
// stopping for idleness at the moment a write's fetch restarted it -- for
// long enough that a new draft has begun by the time the new worker takes
// control. So taking control does not reload the page; it asks again.
// The callbacks themselves are composed and tested in `lib/update.ts`; what
// is wired here is what only the page can do.
const updateSW: ReturnType<typeof registerSW> = deleting ? async () => undefined : registerSW({
  immediate: true,
  onRegisteredSW: (_url, registration) => { if (deleting) void registration?.unregister(); },
  ...updateCallbacks(clean, {
    skip: () => updateSW(true),
    waiting: async () => Boolean((await navigator.serviceWorker.getRegistration())?.waiting),
    reload: () => window.location.reload(),
    // Every store this page has open or is still closing (g67, g71), not
    // `shell.store`: a sign-out, deletion or other tab's logout drops the
    // shell before its store's writes have drained.
    stored: () => pageStores().every(store => store.stored()),
    whenStored: async () => { await Promise.all(pageStores().map(store => store.whenStored())); },
  }),
});
